import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export type DiscussionStatus = 'open' | 'in_progress' | 'waiting_on_solid' | 'waiting_on_client' | 'on_hold' | 'closed' | 'archived';

interface StatusConfig {
  label: string;
  bgColor: string;
  textColor: string;
  dotColor: string;
}

const statusConfigs: Record<DiscussionStatus, StatusConfig> = {
  open: {
    label: 'Open',
    bgColor: 'bg-grey-100',
    textColor: 'text-grey-600',
    dotColor: 'bg-grey-400',
  },
  in_progress: {
    label: 'In Progress',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    dotColor: 'bg-blue-500',
  },
  waiting_on_solid: {
    label: 'Waiting on Solid',
    bgColor: 'bg-primary/10',
    textColor: 'text-primary',
    dotColor: 'bg-primary',
  },
  waiting_on_client: {
    label: 'Waiting on Client',
    bgColor: 'bg-primary/10',
    textColor: 'text-primary',
    dotColor: 'bg-primary',
  },
  on_hold: {
    label: 'On Hold',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-600',
    dotColor: 'bg-orange-500',
  },
  closed: {
    label: 'Closed',
    bgColor: 'bg-grey-100',
    textColor: 'text-grey-400',
    dotColor: 'bg-grey-300',
  },
  archived: {
    label: 'Archived',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    dotColor: 'bg-amber-400',
  },
};

interface StatusDropdownProps {
  status: DiscussionStatus;
  onChange: (status: DiscussionStatus) => void;
  disabled?: boolean;
  className?: string;
}

export function StatusDropdown({
  status,
  onChange,
  disabled = false,
  className,
}: StatusDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentConfig = statusConfigs[status] || statusConfigs.open;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (newStatus: DiscussionStatus) => {
    if (newStatus !== status) {
      onChange(newStatus);
    }
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) setIsOpen(!isOpen);
        }}
        disabled={disabled}
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all',
          currentConfig.bgColor,
          currentConfig.textColor,
          !disabled && 'hover:opacity-80 cursor-pointer',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span className={cn('w-1.5 h-1.5 rounded-full', currentConfig.dotColor)} />
        {currentConfig.label}
        {!disabled && (
          <ChevronDown className={cn('w-3 h-3 transition-transform', isOpen && 'rotate-180')} />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 min-w-[160px]">
          {(Object.entries(statusConfigs) as [DiscussionStatus, StatusConfig][]).map(
            ([statusKey, config]) => (
              <button
                key={statusKey}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelect(statusKey);
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                  statusKey === status
                    ? 'bg-grey-50 font-medium'
                    : 'hover:bg-grey-50'
                )}
              >
                <span className={cn('w-2 h-2 rounded-full', config.dotColor)} />
                <span className={config.textColor}>{config.label}</span>
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

// Export status badge component for read-only display
interface StatusBadgeProps {
  status: DiscussionStatus;
  className?: string;
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const config = statusConfigs[status] || statusConfigs.open;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
        config.bgColor,
        config.textColor,
        className
      )}
    >
      <span className={cn('w-1.5 h-1.5 rounded-full', config.dotColor)} />
      {config.label}
    </span>
  );
}
